import React from 'react';
import { X, FileText } from 'lucide-react';

const TermsOfService = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="terms-modal">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600/20 rounded-lg">
              <FileText className="w-5 h-5 text-indigo-400" />
            </div>
            <h2 className="text-xl font-bold text-white">Terms of Service</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            data-testid="close-terms"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto prose prose-invert prose-slate max-w-none">
          <p className="text-slate-400 text-sm mb-6">Last updated: January 20, 2026</p>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">1. Acceptance of Terms</h3>
            <p className="text-slate-300 leading-relaxed">
              By accessing or using FlowGen.social (&quot;Service&quot;), you agree to be bound by these Terms of Service (&quot;Terms&quot;). If you do not agree to these Terms, do not use the Service. We reserve the right to modify these Terms at any time, and your continued use constitutes acceptance of any changes.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">2. Description of Service</h3>
            <p className="text-slate-300 leading-relaxed">
              FlowGen.social is an AI-powered platform that generates social media content calendars, posts, and images. The Service uses artificial intelligence to create content based on your input and preferences. Generated content is provided as suggestions and should be reviewed before use.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">3. Account Registration</h3>
            <p className="text-slate-300 leading-relaxed mb-3">To use certain features, you must create an account. You agree to:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your password and account</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Not share your account credentials with others</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">4. Subscription and Payments</h3>
            <p className="text-slate-300 leading-relaxed mb-3">Our Service offers various pricing options:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li><strong className="text-white">Pay-Per-Use:</strong> One-time purchases for content calendars</li>
              <li><strong className="text-white">Monthly Subscriptions:</strong> Recurring billing with monthly credits</li>
              <li><strong className="text-white">Lifetime Deals:</strong> One-time payment for permanent access</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-3">
              All payments are processed through Stripe. Prices are in USD unless otherwise stated. Subscriptions auto-renew unless cancelled. Refunds are handled on a case-by-case basis; contact us within 7 days of purchase for refund requests.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">5. Acceptable Use</h3>
            <p className="text-slate-300 leading-relaxed mb-3">You agree NOT to use the Service to:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Generate illegal, harmful, threatening, abusive, or defamatory content</li>
              <li>Create content that infringes on intellectual property rights</li>
              <li>Produce spam, misleading, or deceptive content</li>
              <li>Generate content promoting violence, discrimination, or hate speech</li>
              <li>Attempt to bypass usage limits or security measures</li>
              <li>Resell or redistribute the Service without authorization</li>
              <li>Use automated tools to access the Service excessively</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">6. AI-Generated Content</h3>
            <p className="text-slate-300 leading-relaxed mb-3">Regarding AI-generated content:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Content is generated by AI and may not always be accurate or appropriate</li>
              <li>You are responsible for reviewing and editing content before use</li>
              <li>We do not guarantee that content is free from errors or suitable for any purpose</li>
              <li>You retain ownership of content generated using your inputs</li>
              <li>We may use anonymized data to improve our AI models</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">7. Intellectual Property</h3>
            <p className="text-slate-300 leading-relaxed">
              The Service, including its design, features, and technology, is owned by FlowGen.social and protected by intellectual property laws. You may not copy, modify, distribute, or reverse engineer any part of the Service. Content you create using the Service belongs to you, subject to these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">8. Disclaimer of Warranties</h3>
            <p className="text-slate-300 leading-relaxed">
              THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. WE DISCLAIM ALL WARRANTIES INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">9. Limitation of Liability</h3>
            <p className="text-slate-300 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, FLOWGEN.SOCIAL SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE PAST 12 MONTHS.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">10. Indemnification</h3>
            <p className="text-slate-300 leading-relaxed">
              You agree to indemnify and hold harmless FlowGen.social, its affiliates, and their respective officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Service or violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">11. Termination</h3>
            <p className="text-slate-300 leading-relaxed">
              We may suspend or terminate your access to the Service at any time for violation of these Terms or for any other reason. You may cancel your account at any time through your account settings. Upon termination, your right to use the Service ceases immediately.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">12. Governing Law</h3>
            <p className="text-slate-300 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles. Any disputes shall be resolved in the courts located in the United States.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-white mb-3">13. Contact</h3>
            <p className="text-slate-300 leading-relaxed">
              For questions about these Terms, please contact us at:{' '}
              <a href="mailto:social@coolgeek.me" className="text-indigo-400 hover:text-indigo-300">
                social@coolgeek.me
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
